import React from 'react';
import {SubmitHandler} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import OrderByControlled from 'shared/ui/OrderBy/OrderByControlled';
import Select from 'shared/ui/Select/Select';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import {
    formatHttpIncidentDuration,
    getHttpIncidentResolvedOptions,
    getHttpIncidentSortOptions,
} from 'features/http/lib/httpIncidentFiltersView';
import {IHttpIncidentFiltersForm} from 'features/http/model/HttpIncident/IHttpIncidentFiltersForm';
import useHttpIncidentFiltersForm from 'features/http/model/useHttpIncidentFiltersForm';
import {stringsToDateRange} from 'shared/lib/filterHelper';

import DurationSlider from '../DurationSlider/DurationSlider';

import styles from './HttpIncidentFilters.module.scss';

interface IHttpIncidentFiltersFormProps {
    onClose: () => void;
    ixExport?: boolean;
    onSubmit?: SubmitHandler<IHttpIncidentFiltersForm>;
    isLoading?: boolean
    progress?: number
}

const HttpIncidentFiltersForm: React.FC<IHttpIncidentFiltersFormProps> = ({
    onClose,
    ixExport,
    onSubmit,
    isLoading = false,
    progress,
}) => {
    const {
        form,
        filters,
        sort,
        durationValue,
        handleDurationChange,
        clearResolvedFilter,
        submit,
    } = useHttpIncidentFiltersForm({onClose, isExport: ixExport, onExportSubmit: onSubmit});
    const {register, control, setValue} = form;

    return (
        <form onSubmit={submit}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    label={t`URL или его часть`}
                >
                    <InputText
                        register={register}
                        defaultValue={filters?.['monitor.url']}
                        name="monitor.url"
                        placeholder={t`Введите...`}
                        control={control}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Статус`}
                >
                    <Select
                        control={control}
                        name="resolved"
                        data={getHttpIncidentResolvedOptions()}
                        defaultValue={
                            filters.resolved === undefined
                                ? undefined
                                : String(filters.resolved)
                        }
                        cleanable={true}
                        searchable={false}
                        onChange={(value) => {
                            if (value === null) {
                                clearResolvedFilter();
                            }
                        }}
                    />
                </FormGroupWithError>

                <div className={styles.blockDate}>
                    <FormGroupWithError
                        label={t`Период начала инцидента`}
                    >
                        <DateRangePickerControlled
                            control={control}
                            name="created_at"
                            defaultValue={stringsToDateRange(
                                filters['created_at[after]'],
                                filters['created_at[before]'],
                            )}
                            cleanable={true}
                        />
                    </FormGroupWithError>

                    <FormGroupWithError
                        label={t`Период решения инцидента`}
                    >
                        <DateRangePickerControlled
                            control={control}
                            name="resolved_at"
                            defaultValue={stringsToDateRange(
                                filters['resolved_at[after]'],
                                filters['resolved_at[before]'],
                            )}
                            cleanable={true}
                        />
                    </FormGroupWithError>
                </div>

                <FormGroup>
                    <label htmlFor="duration">
                        {t`Длительность инцидента`}, {t`от`} {formatHttpIncidentDuration(durationValue)}
                    </label>
                    <DurationSlider
                        name="duration"
                        setValue={setValue}
                        defaultValue={filters.duration || 60}
                        onChange={handleDurationChange}
                    />
                </FormGroup>

                <FormGroupWithError
                    label={t`Сортировка`}
                >
                    <OrderByControlled
                        control={control}
                        name="orderBy"
                        defaultValue={sort}
                        data={getHttpIncidentSortOptions()}
                    />
                </FormGroupWithError>

            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={!ixExport ? t`Применить фильтры` : t`Экспортировать`}
                primaryButtonType="submit"
                primaryButtonLoading={isLoading}
                primaryButtonDisabled={isLoading}
                secondaryButtonOnClick={onClose}
                progress={ixExport ? progress : undefined}
            />
        </form>
    );
};

export default HttpIncidentFiltersForm;
